<template>
    <div class="w-full">
        <!-- Sidebar - Added transition classes here -->
        <div class="relative text-white transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]"
            :class="isCollapsed ? 'w-[72px]' : 'laptop:w-[280px] largeDesktop:w-[400px] mobile:w-full'">

            <!-- Toggle Button - Added transition classes -->
            <div class="absolute -top-16 left-0 flex justify-start px-2">
                <button
                    class="transform lg:hover:scale-[1.15] active:scale-[0.98] mobile:hidden laptop:block largeDesktop:block focus:outline-none text-white hover:text-gray-300 transition-transform duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]"
                    @click="toggleSidebar" :class="{ 'rotate-360': isCollapsed }">
                    <img :src="isCollapsed ? showIcon : hideIcon" alt="Toggle Icon"
                        class="min-h-[50px] min-w-[50px] max-h-[50px] max-w-[50px] transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]">
                </button>
            </div>

            <!-- Menu Items - Added transition classes -->
            <nav :class="isCollapsed ? 'bg-white rounded-full px-[7px] py-[3px]' : 'bg-transparent pb-3'"
                class="transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]">
                <ul class="flex flex-col gap-2 p-0 m-0">
                    <li v-for="item in menuItems" :key="item.name"
                        class="menuList overflow-hidden relative group mobile:border-[1px] rounded-[20px] mobile:py-0 tablet:py-1 tablet:transition-all tablet:duration-500 tablet:ease-[cubic-bezier(0.4,0,0.2,1)]"
                        :class="[getMenuItemClasses(item), (!isCollapsed && !(item.submenu && activeMenu === item.name)) ? 'lg:hover:translate-x-2' : '']" :style="getMenuItemStyles(item)">
                        <!-- Main link - Added transition classes -->
                        <div v-if="item.submenu"
                            :class="isCollapsed ? '!border-0 !bg-transparent !p-0' : 'mobile:p-1 tablet:p-2 flex-row '"
                            class="transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]"> 
                            <div class="flex flex-grow-1 items-center cursor-pointer transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]"
                                :class="isCollapsed ? '!gap-0 w-auto' : 'gap-2 w-full'" @click="handleMenuClick(item)">
                                <!-- Icon container -->
                                <div
                                    class="min-w-[58px] min-h-[58px] transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] overflow-hidden relative">
                                    <img :src="require(`@/assets/images/${item.icon}`)" :alt="item.name"
                                        class="w-[58px] h-[58px] transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]">
                                </div>
                                <!-- Text and label - Animate opacity for smoother disappearance -->
                                <div v-if="sideMenuText" class="flex flex-grow-1 items-center justify-between w-full "
                                    :class="isCollapsed ? 'opacity-0 absolute' : 'opacity-100 relative transition-all duration-500 delay-500  ease-[cubic-bezier(0,0,0,0.5)]'">
                                    <span 
                                        class="text-[18px] font-roboto font-bold text-black-100 transition-all duration-[2000ms] ease-[cubic-bezier(0,0,0,0.5)]">
                                        {{ item.name }}
                                        <span v-if="item.comingSoon"
                                            class="text-xs  ml-1 font-roboto text-[#999999]">(Coming
                                            Soon)</span>
                                    </span>
                                    <div class="flex items-center gap-3">
                                        <div v-if="!isCollapsed && item.submenu"
                                            class="transition-all duration-100 ease-[cubic-bezier(0,0,0,0.5)] flex items-center justify-center w-8 h-8 transform"
                                            :class="{ 'rotate-0': item.submenu && (!isCollapsed && activeMenu === item.name), 'rotate-180': !(item.submenu && (!isCollapsed && activeMenu === item.name)) }">
                                            <i
                                                class="fa fa-angle-up !text-[30px] font-bold text-gray-600 transition-all duration-500 ease-[cubic-bezier(0,0,0,0.5)]"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-else @click="handleMenuClick(item)" :class="[
                            isCollapsed ? '!border-0 !bg-transparent !p-0' : 'mobile:p-1 tablet:p-2 flex-row',
                            isRouteActive(item) ? 'your-active-extra-class' : ''
                        ]" class="transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]">
                            <div class="flex items-center cursor-pointer transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]"
                                :class="isCollapsed ? '!gap-0 w-auto' : 'gap-2 w-full'">
                                <!-- Icon container -->
                                <div
                                    class="min-w-[58px] min-h-[58px] transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] overflow-hidden relative">
                                    <img :src="require(`@/assets/images/${item.icon}`)" :alt="item.name"
                                        class="min-w-[58px] min-h-[58px] transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]">
                                </div>
                                <!-- Text and label - Animate opacity for smoother disappearance -->
                                <div v-if="sideMenuText" class="flex flex-grow-1 items-center justify-between w-full "
                                    :class="isCollapsed ? 'invisible opacity-0 absolute' : 'visible opacity-100 relative transition-all duration-500 delay-500 ease-[cubic-bezier(0,0,0,0.5)]'">
                                    <span
                                        class="text-[18px] font-roboto font-bold text-black-100 transition-all duration-[2000ms] ease-[cubic-bezier(0,0,0,0.5)]">
                                        {{ item.name }}
                                        <span v-if="item.comingSoon"
                                            class="text-xs  ml-1 font-roboto text-[#999999]">(Coming
                                            Soon)</span>
                                    </span>
                                    <div class="flex items-center gap-3">
                                        <span v-if="getPlanCount('bundle') > 0"
                                            class="inline-block text-sm min-w-10 text-center font-bold px-2 rounded-full text-blueLight-100 bg-gray-200 transition-all duration-100 ease-[cubic-bezier(0,0,0,0.5)]">
                                            {{ getPlanCount('bundle') }}
                                        </span>
                                        <div v-if="!isCollapsed && item.submenu"
                                            class="transition-all duration-100 ease-[cubic-bezier(0,0,0,0.5)] flex items-center justify-center w-8 h-8 transform"
                                            :class="{ 'rotate-0': item.submenu && (!isCollapsed && activeMenu === item.name), 'rotate-180': !(item.submenu && (!isCollapsed && activeMenu === item.name)) }">
                                            <i
                                                class="fa fa-angle-up !text-[30px] font-bold text-gray-600 transition-all duration-500 ease-[cubic-bezier(0,0,0,0.5)]"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Submenu - Added transition classes -->
                        <ul v-if="item.submenu && (!isCollapsed && activeMenu === item.name)"
                            class="p-2 flex flex-col gap-1 transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] origin-top">
                            <!-- {{ item.submenu }} -->
                            <div v-for="sub in item.submenu" :key="sub.name">
                                <li class="relative group/submenu text-md font-roboto pl-6 pr-1 py-1.5 cursor-pointer rounded-lg border border-[#CCCCCC] text-black-100 transition-all duration-300"
                                    :class="{
                                        'active-route-class': isRouteActive(sub),
                                        'bg-customBlue-100': sub.isOpen,
                                        '!bg-customBlue-100/5 !border-customBlue-100/10': !sub.isOpen
                                    }"
                                    :style="(sub._hover || isRouteActive(sub)) ? 'background-color: ' + (item.menuHoverColor || '#4b5563') + ' !important; color: white !important;' : ''"
                                    @mouseover="sub._hover = true" @mouseleave="sub._hover = false"
                                    @click.stop="() =>  handleSubmenuClick(sub)">
                                    <!-- Submenu content -->
                                    <div class="flex justify-between items-center transition-all duration-800">
                                        <div class="rounded px-1.5 py-0.5 transition-all duration-300" :class="{
                                            'text-[#fff]': sub.isOpen,
                                        }">
                                            {{ sub.name }}
                                            <span v-if="sub.comingSoon"
                                                class="text-xs  ml-1 font-roboto text-[#999999] group-hover/submenu:text-white transition-all duration-300">(Coming
                                                Soon)</span>
                                        </div>
                                        <div v-if="sub.submenu" @click.stop="handleSubmenuClick(sub)"
                                            class="transition-all duration-800 flex items-center justify-center w-6 h-6"
                                            :class="{ 'rotate-180': sub.isOpen }">
                                            <i class="fa fa-angle-down !text-[24px] font-bold group-hover/submenu:!text-white transition-all duration-300"
                                                :class="isRouteActive(sub) || sub.isOpen ? 'text-white' : 'text-gray-600'"></i>
                                        </div>
                                        <div
                                            class="absolute left-2 h-full flex items-center transition-all duration-300">
                                            <span class="block w-2.5 h-2.5 rounded-full transition-all duration-300" :class="{
                                                '!bg-[#fff]': sub.isOpen,
                                            }" :style="{
                                                backgroundColor: (sub._hover || isRouteActive(sub)) ? '#ffffff' : (item.menuHoverColor || '#4b5563')
                                            }"></span>
                                        </div>
                                    </div>
                                </li>

                                <!-- Nested Submenu - Added transition classes -->
                                <div v-if="sub.submenu && sub.isOpen"
                                    class="py-3 transition-all duration-300 ease-in-out origin-top">
                                    <ul
                                        class="ml-4 mt-1 px-2 py-0 flex flex-col gap-1 relative rounded-lg transition-all duration-300">
                                        <div
                                            class="w-[3px] h-full absolute left-2 top-0 rounded-full bg-gray-300 transition-all duration-300">
                                        </div>
                                        <li v-for="nestedSub in sub.submenu" :key="nestedSub.name"
                                            class="relative group-childMenu text-md font-roboto flex justify-between items-center pl-3 pr-3 py-1.5 cursor-pointer rounded-lg bg-white text-black-100 transition-all duration-300"
                                            :class="{ 'active-route-class': isRouteActive(nestedSub) }"
                                            :style="(nestedSub._hover || isRouteActive(nestedSub)) ? 'color: ' + (item.menuHoverColor || '#4b5563') + '; ' : ''"
                                            @mouseover="nestedSub._hover = true" @mouseleave="nestedSub._hover = false"
                                            @click="handleSubmenuClick(nestedSub)">
                                            {{ nestedSub.name }}
                                            <!-- plan cart count hidden -->
                                            <span v-if="nestedSub.label && getPlanCount(nestedSub.label) > 0"
                                                class="hidden inline-block text-sm min-w-10 text-center font-bold px-2 rounded-full text-[#2459A8] bg-gray-200 transition-all duration-100 ease-[cubic-bezier(0,0,0,0.5)]">
                                                {{ getPlanCount(nestedSub.label) }}
                                            </span>
                                            <div 
                                                class="absolute top-0 left-0 h-full flex items-center transition-all duration-300">
                                                <span
                                                    class="block w-[3px] h-full rounded-full transition-all duration-300"
                                                    :style="{
                                                        backgroundColor: (nestedSub._hover || isRouteActive(nestedSub)) ? '#F5B134 ' : '#d1d5db'
                                                    }"></span>
                                            </div>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </ul>
                    </li>
                </ul>
            </nav>
        </div>
    </div>
</template>

<script>
import navShowIcon from "../assets/images/nav-show-icon.svg"
import navHideIcon from "../assets/images/nav-hide-icon.svg"
import { mapActions, mapGetters } from "vuex";

export default {
    props: ['setSideMenuClicked'],
    data() {
        return {
            user: JSON.parse(localStorage.getItem('user')) ?? null,
            isCollapsed: false,
            activeMenu: '',
            showIcon: navShowIcon,
            hideIcon: navHideIcon,
            isHovered: false,
            cartCount: {},
            menuItems: [],
            menuDataLoaded: false,
            sideMenuText: true,
        };
    },
    computed: {
        ...mapGetters(["sideMenuData", "cartCountResponse", "userObject"]),
        currentRoute() {
            return this.$route.path;
        },
    },
    watch: {
        '$route.path'(newPath) {
            this.$nextTick(() => {
                if (this.menuDataLoaded) {
                    this.activateMenuByRoute();
                }
            });
        },

        sideMenuData: {
            handler: function (data) {
                if (data && data.length > 0) {
                    this.menuItems = JSON.parse(JSON.stringify(data));
                    this.menuDataLoaded = true;

                    this.$nextTick(() => {
                        this.initSubmenus();
                        this.activateMenuByRoute();
                    });
                }
            },
            immediate: true,
            deep: true
        },

        cartCountResponse: function (data) {
            this.cartCount = this.cartCountResponse;
        },

        userObject: function () {
            localStorage.setItem('user', JSON.stringify(this.userObject));
        },
    },
    created() {
        this.initSubmenus();
    },
    mounted() {
        this.getProfile({});
        this.setMenu();

        // Restore only sidebar collapse state from localStorage
        const savedSidebarState = localStorage.getItem('sidebarCollapsed');
        if (savedSidebarState != null) {
            this.isCollapsed = savedSidebarState == 'true';
        }

        this.$nextTick(() => {

            const is_from_mobile = localStorage.getItem("is_from_mobile", 0) ?? 0;
            if ((localStorage.getItem('user') ?? null) != null && is_from_mobile != '1') {
                if (this.$helper.isEmpty(this.$store.state.product.cartCountResponse)) {
                    this.getCartCount({});
                } else {
                    this.cartCount = this.$store.state.product.cartCountResponse;
                }
            }
        });
    },
    methods: {
        ...mapActions(["getSideMenu", "getCartCount", "setSelectedProtectionPlan", "resetSate", "getProfile"]),

        getPlanCount(plan) {
            return this.cartCount[plan] ?? null;
        },

        toggleSidebar() {
            this.isCollapsed = !this.isCollapsed;
            this.activeMenu = '';
            this.sideMenuText = !this.sideMenuText;          
            // Save only sidebar collapse state
            localStorage.setItem('sidebarCollapsed', this.isCollapsed);
            this.$emit('sidebar-collapse', this.isCollapsed);
            setTimeout(() => {
                this.sideMenuText = true;            
            }, 500);
        },

        handleMenuClick(item) {
            if (item.name == 'Home Essentials Bundles' && item.route != this.$route.path) {
                this.resetSate();
            }
            if (this.isCollapsed) {
                this.toggleSidebar()
            }

            if (item.route && !item.submenu) {
                this.isCollapsed = false,
                this.activeMenu = '',
                this.$router.push(item.route);
                if (this.setSideMenuClicked) {
                    this.setSideMenuClicked();
                }
                return;
            }

            // Toggle the active menu - NO localStorage usage
            if (this.activeMenu === item.name) {
                this.activeMenu = '';
            } else {
                this.activeMenu = item.name;
            }
        },

        handleSubmenuClick(subItem) {
            if (subItem.submenu) {
                // Toggle nested submenu - NO localStorage usage
                subItem.isOpen = !subItem.isOpen;
            } else if (subItem.route) {
                if (this.setSideMenuClicked) {
                    this.setSideMenuClicked();
                }
                this.$router.push(subItem.route);
                // this.setSelectedProtectionPlan(subItem);

                if (this.isCollapsed) {
                    this.activeMenu = '';
                }
            }
        },

        // Fixed: Now sets activeMenu to the TOP-LEVEL parent (Partner) instead of immediate parent (NRG)
        activateMenuByRoute() {
            if (!this.menuDataLoaded || this.menuItems.length === 0) {
                return;
            }
            // partner checkout default omega plan select
            const currentRoute = (this.$route.name == 'partnerCheckout') ? '/protection-plan/myhappyhome/products' : this.$route.path;

            // Reset all menu states first
            this.resetAllMenuStates();

            let foundRoute = false;
            let matchedItem = null; // Store the matched menu item

            // Find the menu item that matches the current route and open ALL parent menus
            const findAndActivateRoute = (items, parentChain = []) => {
                for (const item of items) {
                    // Check if this item matches the current route
                    if (item.route === currentRoute) {
                        matchedItem = item; // Store the matched item

                        if (parentChain.length > 0) {
                            // Set activeMenu to the TOP-LEVEL parent (first in chain)
                            const topLevelParent = parentChain[0];
                            this.activeMenu = topLevelParent.name;

                            // Open ALL parent menus in the chain (both Partner and NRG)
                            parentChain.forEach(parent => {
                                if (parent.submenu) {
                                    parent.isOpen = true;
                                }
                            });
                        }

                        foundRoute = true;
                        return true;
                    }

                    // Recursively check submenus
                    if (item.submenu) {
                        const found = findAndActivateRoute(item.submenu, [...parentChain, item]);
                        if (found) {
                            foundRoute = true;
                            return true;
                        }
                    }
                }
                return false;
            };

            // Try to find and activate based on current route
            findAndActivateRoute(this.menuItems);

            // Call setSelectedProtectionPlan if a route was matched
            if (foundRoute && matchedItem) {
                this.setSelectedProtectionPlan(matchedItem);
            }

            if (!foundRoute) {
                this.activeMenu = '';
            }

        },

        // Helper to reset all menu open states
        resetAllMenuStates() {
            const resetItems = (items) => {
                items.forEach(item => {
                    if (item.submenu) {
                        item.isOpen = false;
                        resetItems(item.submenu);
                    }
                });
            };
            resetItems(this.menuItems);
        },

        initSubmenus() {
            const walk = items => {
                items.forEach(item => {
                    item._hover = false;
                    if ('isOpen' in item) item.isOpen = false;
                    if (item.submenu) walk(item.submenu);
                });
            };
            walk(this.menuItems);
        },

        isRouteActive(item) {
            if (item.route) return this.currentRoute === item.route;
            if (item.submenu) return item.submenu.some(sub => this.isRouteActive(sub));
            return false;
        },

        getMenuItemClasses(item) {
            const isActive = this.isRouteActive(item);
            return [
                this.isCollapsed ? '!border-0 !bg-transparent' : ' bg-white mobile:shadow-[0_0px_15px_-5px_#b5b2b2]',
                isActive ? 'active-route-class' : '',
            ];
        },

        getMenuItemStyles(item) {
            const isActive = this.isRouteActive(item);

            if (isActive) {
                return {
                    'border-color': item.menuHoverColor || '#2459A8',
                    'border-style': 'solid',
                    'border-radius': '1rem'
                };
            }

            // Submenu + active menu logic
            if (item.submenu && (!this.isCollapsed && this.activeMenu === item.name)) {
                return {
                    'border-color': item.menuHoverColor || '#2459A8',
                    'border-style': 'solid',
                    'border-radius': '1rem',
                };
            }

            return {};
        },

        setMenu() {
            this.getSideMenu({});
        },

        // Old menu setup methods (keep for reference)
        addMenu(item) {
            this.menuItems.push(item);
        },

        addSubMenu(parentName, item) {
            const parent = this.findMenuByName(parentName, this.menuItems);
            if (parent) {
                parent.submenu.push(item);
            }
        },

        removeSubMenu(parentName, subMenuName) {
            const parent = this.findMenuByName(parentName, this.menuItems);
            if (parent && parent.submenu) {
                parent.submenu = parent.submenu.filter(sub => sub.name !== subMenuName);
            }
        },

        findMenuByName(name, items) {
            for (let item of items) {
                if (item.name === name) return item;
                if (item.submenu && item.submenu.length) {
                    let found = this.findMenuByName(name, item.submenu);
                    if (found) return found;
                }
            }
            return null;
        },
    }
};
</script>